import { observer } from 'mobx-react';
import AppStore from '../../app.store';
import { HeaderLayout } from '../../styles/header-layout';
import { Wrapper, Main } from '../../styles/wrapper';
import { Modal as AntModal, Switch } from 'antd';
import { useEffect, useMemo, useRef, useState } from 'react';
import Modal from '../modal/modal.view';
import ConfirmLogout from '../confirm/confirm.logout';
import { useNavigate } from 'react-router-dom';
import { LogoutItem } from '../confirm/confirm.logout';
import ProfileMenu from './subMenu';
import SettingsCreateProfile from './settings.modal/settings.create-profile';
import SettingsCreatePassword from './settings.modal/settings.create-password';
import {MutedIcon} from '../../styles/iconStyles';
import BELL from '../../assets/icons/bell.svg';
import styled, { keyframes, ThemeProvider } from 'styled-components';
import FirebaseStore from './firebase.store';
import {getFirebase} from '../../firebase.loader';
import { Layout } from '../../styles/layout';
import { primaryColor, primaryForeground, primaryHover, mutedForeground, shellDivider, primaryColorTransparent } from '../../utils/variables';
import DestroyAllStore from "../../utils/destroyAll";
import CashSessionsStore from "../../pages/cash_sessions/cash_sessions.store";
import { hasPermission } from "../../utils/hasPermission";
import { PermissionGroup } from "../../utils/permission";
import { redirectToLanding } from "../../utils/greeting";

import { OpenCashSessionModal } from "../../pages/cash_sessions/cash_sessions.modal/cash_session.open.modal";
import { CloseCashSessionModal } from "../../pages/cash_sessions/cash_sessions.modal/cash_session.close.modal";
import { ChatModalView } from "../chat/chat.modal.view";
import ThemeToggle from '../theme-toggle/theme-toggle';
import { useIsPhone } from '../../hooks/useMediaQuery';
import { AssistantIcon } from './assistant-icon';

const HEADER_BACKGROUND_REQUEST_DEFER_MS = 2000;
const HEADER_CASH_SESSION_DEFER_MS = 1000;

const assistantFloat = keyframes`
  0%, 100% { transform: translateY(0); }
  50% { transform: translateY(2px); }
`;
const textShimmer = keyframes`
  0%, 100% { text-shadow: 0 0 0 rgba(255,255,255,0); }
  50% { text-shadow: 0 0 6px rgba(255,255,255,0.35); }
`;

const AssistantButton = styled.button<{ $iconOnly?: boolean }>`
  padding: ${({ $iconOnly }) => ($iconOnly ? '8px' : '6px 14px')};
  border-radius: 8px;
  border: none;
  cursor: pointer;
  font-size: 13px;
  font-weight: 600;
  color: ${primaryForeground};
  background: ${primaryColor};
  box-shadow: 0 2px 8px rgba(43, 127, 255, 0.3);
  transition: background-color 0.2s ease, box-shadow 0.2s ease, transform 0.2s ease;
  animation: ${assistantFloat} 3s ease-in-out infinite;
  display: inline-flex;
  align-items: center;
  justify-content: center;
  min-width: ${({ $iconOnly }) => ($iconOnly ? '36px' : 'auto')};
  height: ${({ $iconOnly }) => ($iconOnly ? '36px' : 'auto')};
  flex-shrink: 0;

  &:hover {
    background: ${primaryHover};
    box-shadow: 0 4px 12px rgba(43, 127, 255, 0.35);
    transform: translateY(-1px);
  }
  &:hover span {
    text-shadow: 0 0 10px rgba(255, 255, 255, 0.6);
  }
  &:active {
    transform: translateY(0);
  }
`;

const AssistantButtonText = styled.span`
  animation: ${textShimmer} 2.5s ease-in-out infinite;
  transition: text-shadow 0.25s ease;
`;

const Header = observer(() => {
  const { isNavOpen, userData, isMacOS, permissions } = AppStore;
  const [confirmLogoutModal, setConfirmLogoutModal] = useState<LogoutItem>({
    isModalOpen: false,
  });

  const { clearAuthSession } = AppStore;
  const navigate = useNavigate();
  const {destroyAll} = DestroyAllStore;

  const deleteToken = async () => {
    const firebase = await getFirebase();
    await firebase.removeDeviceToken();
    destroyAll();
    clearAuthSession();
    redirectToLanding();
  }

  const handleLogout = () => {
    destroyAll();
    setConfirmLogoutModal({ isModalOpen: true });
  };

  const [editSettingsProfile, setEditSettingsProfile] = useState(false);
  const [editSettingsPassword, setEditSettingsPassword] = useState(false);

  const [visible, setVisible] = useState(false);

  const showModal = () => {
    setVisible(true);
    if (!firebaseapi?.length) {
      const controller = new AbortController();
      void firebasePageInit(controller.signal);
    }
  };

  const handleCancel = () => {
    setVisible(false);
  };

  const { firebaseapi, firebasePageInit, firebaseReq, firebaseTotal, popupEnabled, setPopupEnabled } = FirebaseStore;

  useEffect(() => {
    if (!visible) {
      return undefined;
    }
    FirebaseStore.suppressPopups();
    return () => {
      FirebaseStore.restorePopups();
    };
  }, [visible]);

  const { currentSession } = CashSessionsStore;
  const [isOpenCashModalActive, setIsOpenCashModalActive] = useState(false);
  const [isCloseCashModalActive, setIsCloseCashModalActive] = useState(false);
  const [isChatModalOpen, setIsChatModalOpen] = useState(false);

  useEffect(() => {
    if (!userData?.business_unit_id || !permissions) {
      return undefined;
    }
    if (!hasPermission(permissions, PermissionGroup.CASHIER_SESSION, 'READ')) {
      return undefined;
    }

    const timer = window.setTimeout(() => {
      void CashSessionsStore.fetchCurrentSession(userData.business_unit_id);
    }, HEADER_CASH_SESSION_DEFER_MS);

    return () => window.clearTimeout(timer);
  }, [userData, permissions]);

  useEffect(() => {
    const controller = new AbortController();
    const timer = window.setTimeout(() => {
      void firebasePageInit(controller.signal);
    }, HEADER_BACKGROUND_REQUEST_DEFER_MS);

    return () => {
      window.clearTimeout(timer);
      controller.abort();
    };
  }, [firebasePageInit]);

  const unreadNotifications = firebaseapi?.filter(
    (item) => item.read === false
  );

  const handleOpenNotificationsPage = () => {
    setVisible(false);
    navigate('/notifications');
  };

  const [onlyUnread, setOnlyUnread] = useState(false);
  const [modalLoading, setModalLoading] = useState(false);
  const isModalLoadingRef = useRef(false);
  const isMobileView = useIsPhone();

  const unreadIds = useMemo(
    () => firebaseapi?.filter((item) => item.read === false).map((item) => item.id) ?? [],
    [firebaseapi]
  );

  const resetModalNotifications = async () => {
    setModalLoading(true);
    isModalLoadingRef.current = true;
    await firebaseReq({ limit: 10, offset: 0, onlyUnread, append: false });
    setModalLoading(false);
    isModalLoadingRef.current = false;
  };

  const loadMoreModalNotifications = async () => {
    if (isModalLoadingRef.current) {
      return;
    }
    const current = firebaseapi?.length ?? 0;
    if (current >= firebaseTotal) {
      return;
    }
    setModalLoading(true);
    isModalLoadingRef.current = true;
    await firebaseReq({ limit: 10, offset: current, onlyUnread, append: true });
    setModalLoading(false);
    isModalLoadingRef.current = false;
  };

  useEffect(() => {
    if (!visible) {
      return;
    }
    void resetModalNotifications();
  }, [visible, onlyUnread]);

  useEffect(() => {
    if (!visible || unreadIds.length === 0) {
      return;
    }
    void FirebaseStore.sendNotesIds(unreadIds);
    if (onlyUnread) {
      void resetModalNotifications();
    }
  }, [visible, unreadIds, onlyUnread]);

  const AnyThemeProvider = ThemeProvider as any;

  return (
    <>
      <HeaderLayout isNavOpen={isNavOpen}>
        <Wrapper withoutMaxWidth>
          <Layout vAlign="center" between={15}>
            {userData?.role_id === 1 && (
              <AssistantButton
                type="button"
                onClick={() => setIsChatModalOpen(true)}
                $iconOnly={isMobileView}
                title="Помощник"
                aria-label="Помощник"
              >
                {isMobileView ? <AssistantIcon /> : <AssistantButtonText>Помощник</AssistantButtonText>}
              </AssistantButton>
            )}
            {permissions && hasPermission(permissions, PermissionGroup.CASHIER_SESSION, 'READ') && (
              <div
                style={{
                  cursor: 'pointer',
                  padding: '4px 12px',
                  borderRadius: '16px',
                  backgroundColor: currentSession ? 'rgba(82, 196, 26, 0.1)' : 'rgba(255, 77, 79, 0.1)',
                  border: `1px solid ${currentSession ? '#52c41a' : '#ff4d4f'}`,
                  color: currentSession ? '#52c41a' : '#ff4d4f',
                  fontWeight: 'bold',
                  fontSize: '12px',
                  whiteSpace: 'nowrap',
                  transition: 'all 0.3s'
                }}
                onClick={() => currentSession ? setIsCloseCashModalActive(true) : setIsOpenCashModalActive(true)}
              >
                {isMobileView ? 'Касса' : (currentSession ? 'Касса открыта' : 'Касса не открыта')}
              </div>
            )}
            <ThemeToggle />
            <AnyThemeProvider theme={{}}>
              <div style={{ display: 'flex', alignItems: 'center', position: 'relative' }}>
                <MutedIcon src={BELL} bell={true} extent={'18px'} onClick={showModal} />
                {unreadNotifications?.length ? (
                  <span
                    style={{
                      background: '#ff4d4f',
                      minWidth: '14px',
                      height: '14px',
                      borderRadius: '50%',
                      position: 'absolute',
                      right: '-6px',
                      top: '-6px',
                      fontSize: '10px',
                      display: 'flex',
                      alignItems: 'center',
                      justifyContent: 'center',
                      color: primaryForeground,
                      padding: '0 4px',
                      fontWeight: 'bold',
                      zIndex: 1,
                      cursor: 'pointer'
                    }}
                    onClick={handleOpenNotificationsPage}
                  >
                    {unreadNotifications.length}
                  </span>
                ) : null}
              </div>
              <AntModal
                open={visible}
                onCancel={handleCancel}
                title="Уведомления"
                style={
                  isMobileView
                    ? { top: 0, margin: 0, padding: 0, left: 0, right: 0 }
                    : { top: 70, marginRight: 85 }
                }
                width={isMobileView ? '100vw' : 460}
                bodyStyle={
                  isMobileView
                    ? { height: '100vh', padding: '16px', display: 'flex', flexDirection: 'column' }
                    : undefined
                }
                footer={null}
              >
                <div
                  style={{
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'space-between',
                    marginBottom: 12,
                    flexWrap: 'wrap',
                    gap: 8,
                  }}
                >
                  <div style={{ display: 'flex', alignItems: 'center', gap: 12, flexWrap: 'wrap' }}>
                    <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
                      <span style={{ color: mutedForeground, fontSize: 12 }}>
                        Всплывающие
                      </span>
                      <Switch size="small" checked={popupEnabled} onChange={setPopupEnabled} />
                    </div>
                    <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
                      <span style={{ color: mutedForeground, fontSize: 12 }}>
                        Только новые
                      </span>
                      <Switch size="small" checked={onlyUnread} onChange={setOnlyUnread} />
                    </div>
                  </div>
                  <div
                    style={{
                      display: 'flex',
                      alignItems: 'center',
                      gap: 10,
                      flexWrap: 'wrap',
                      justifyContent: isMobileView ? 'flex-start' : 'flex-end',
                    }}
                  >
                    <span
                      style={{ color: primaryColor, fontSize: 12, cursor: 'pointer' }}
                      onClick={() => {
                        void FirebaseStore.markAllRead();
                        void resetModalNotifications();
                      }}
                    >
                      Отметить все прочитанным
                    </span>
                    <span
                      style={{ color: primaryColor, fontSize: 12, cursor: 'pointer' }}
                      onClick={handleOpenNotificationsPage}
                    >
                      Все уведомления
                    </span>
                  </div>
                </div>
                <hr
                  style={{
                    height: '1px',
                    borderWidth: 0,
                    backgroundColor: shellDivider,
                  }}
                />
                <Main
                  style={
                    isMobileView
                      ? { flex: 1, minHeight: 0, overflowY: 'auto' }
                      : { maxHeight: 420, overflowY: 'scroll' }
                  }
                  isMacOS={isMacOS}
                  onScroll={(event) => {
                    const target = event.currentTarget;
                    if (target.scrollTop + target.clientHeight >= target.scrollHeight - 40) {
                      void loadMoreModalNotifications();
                    }
                  }}
                >
                  <div className="main-modal">
                    {firebaseapi?.map((firebase, index) => (
                      <div
                        key={index}
                        style={{
                          display: 'flex',
                          alignItems: 'center',
                          justifyContent: 'space-between',
                          marginTop: '15px',
                          borderRadius: '5px',
                          backgroundColor: firebase.read ? 'transparent' : primaryColorTransparent,
                          cursor: 'pointer'
                        }}
                        onClick={handleOpenNotificationsPage}
                      >
                        <div className="modal_data">
                          <div className="modal_title">{firebase.title}</div>
                          <div className="modal_body">{firebase.body}</div>
                        </div>
                        <div style={{ width: 90 }}>{firebase.formatted_date}</div>
                      </div>
                    ))}
                    {modalLoading && (
                      <div style={{ padding: '8px 0', textAlign: 'center', color: mutedForeground }}>
                        Загрузка...
                      </div>
                    )}
                  </div>
                </Main>
              </AntModal>
            </AnyThemeProvider>
          </Layout>
        </Wrapper>
        <Wrapper withoutMaxWidth>
          <ProfileMenu
            onLogout={handleLogout}
            onOpenProfile={() => setEditSettingsProfile(true)}
            showAssistant={userData?.role_id === 1}
            onOpenAssistant={() => setIsChatModalOpen(true)}
          />
        </Wrapper>
        {editSettingsProfile && (
          <Modal
            title={'Профиль'}
            onClose={() => {
              setEditSettingsProfile(false);
            }}
          >
            <SettingsCreateProfile
              setModal={setEditSettingsProfile}
              setChangePasswordModal={setEditSettingsPassword}
            />
          </Modal>
        )}
        {editSettingsPassword && (
          <Modal
            title={'Изменение пaроля'}
            onClose={() => {
              setEditSettingsPassword(false);
            }}
          >
            <SettingsCreatePassword
              setModal={setEditSettingsPassword}
            ></SettingsCreatePassword>
          </Modal>
        )}
      </HeaderLayout>
      {confirmLogoutModal.isModalOpen && (
        <Modal
          title="Выход из приложения"
          onClose={() => {
            setConfirmLogoutModal({ isModalOpen: false });
          }}
        >
          <ConfirmLogout
            warningText={`Подтвердите данное действие?`}
            setModal={setConfirmLogoutModal}
            callback={deleteToken}
            isLoading={false}
          />
        </Modal>
      )}
      {isOpenCashModalActive && (
          <Modal title="Открыть кассовую сессию" onClose={() => setIsOpenCashModalActive(false)}>
              <OpenCashSessionModal onClose={() => setIsOpenCashModalActive(false)} />
          </Modal>
      )}
      {isCloseCashModalActive && currentSession && (
          <Modal title="Закрыть кассовую сессию" onClose={() => setIsCloseCashModalActive(false)}>
              <CloseCashSessionModal
                  sessionId={currentSession.id}
                  onClose={() => setIsCloseCashModalActive(false)}
              />
          </Modal>
      )}
      {userData?.role_id === 1 && isChatModalOpen && (
          <ChatModalView onClose={() => setIsChatModalOpen(false)} />
      )}
    </>
  );
});

export default Header;
